/**
 * Marcas y Modelos — estandarización del catálogo (programa "marcasmodelos").
 *
 * marca/modelo viven como texto libre copiado a producto, factmot y demás
 * tablas, así que "HONDA", "Honda " y "HODNA" conviven y parten reportes,
 * conteos por marca y el catálogo de precios. Esta pantalla tiene dos vistas:
 *
 *  - Variantes detectadas: combinaciones marca/modelo de los datos vivos
 *    (unidades no vendidas/incautadas/en tránsito y préstamos activos) que
 *    no matchean el catálogo, con una sugerencia del parecido más cercano.
 *    Cada variante se puede aceptar como entrada canónica nueva o fusionar
 *    hacia una entrada existente (el backend solo toca datos vivos y pide
 *    confirmación mostrando cuántas filas cambiarán, mismo contrato que
 *    "Aplicar precios al inventario").
 *
 *  - Catálogo: el CRUD de marcamodelo embebido desde Configuración
 *    (CatalogosConfig con soloCatalogo), sin duplicar pantalla.
 *
 *  - Revisar con IA: el negocio pide propuestas contra la lista estándar
 *    compartida y acepta las que quiera; lo aceptado es solo suyo (ver
 *    revisionia.js).
 *
 * Permisos: los devuelve el backend (rolesusuarios programa "marcasmodelos",
 * con acceso transitorio por la fila de "configuracion" mientras se siembra;
 * ver permisos.py del server). modificar = aceptar variantes / fusionar.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import LibraryAddCheckOutlinedIcon from "@mui/icons-material/LibraryAddCheckOutlined";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import CatalogosConfig from "modules/configuracion/catalogosconfig";
import RevisionIA from "modules/marcasmodelos/revisionia";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const etiquetaCatalogo = (c) => `${c.marca} ${c.modelo}`;

function SinVariantes() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Todo el inventario vivo coincide con el catálogo
      </Typography>
    </Stack>
  );
}

export default function MarcasModelos() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [permisos, setPermisos] = useState({ modificar: "N", borrar: "N" });
  const [variantes, setVariantes] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const puedeModificar = String(permisos.modificar || "N").toUpperCase() === "S";

  const cargar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/marcasmodelos_variantes", {});
      if (ok) {
        setPermisos(data.permisos || { modificar: "N", borrar: "N" });
        setCatalogo(data.catalogo || []);
        setVariantes((data.variantes || []).map((v) => ({ ...v, id: `${v.marca}|${v.modelo}` })));
      } else {
        setVariantes([]);
        showAlert({ message: data.Error || "No se pudieron leer las variantes", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Aceptar una variante como entrada canónica del catálogo ----
  const [agregando, setAgregando] = useState(null); // variante o null
  const [agregandoRun, setAgregandoRun] = useState(false);

  const agregar = async () => {
    setAgregandoRun(true);
    try {
      const { ok, data } = await post("/api/marcasmodelos_agregar", {
        marca: agregando.marca,
        modelo: agregando.modelo,
      });
      if (ok) {
        showAlert({ message: "Agregado al catálogo", color: "success" });
        setAgregando(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo agregar al catálogo", color: "error" });
      }
    } finally {
      setAgregandoRun(false);
    }
  };

  // ---- Fusionar una variante hacia una entrada del catálogo ----
  // Al elegir destino se consulta (sin confirmar) cuántas unidades vivas y
  // préstamos activos cambiarían; Fusionar ejecuta con confirmar.
  const [fusion, setFusion] = useState(null); // {variante, destino, conteo} o null
  const [fusionRun, setFusionRun] = useState(false);

  const consultarConteo = async (variante, destino) => {
    if (!destino) return;
    const { ok, data } = await post("/api/marcasmodelos_fusionar", {
      marca: variante.marca,
      modelo: variante.modelo,
      id: destino.id,
    });
    setFusion((f) => {
      // La respuesta puede llegar tarde: solo aplica si el diálogo sigue
      // abierto sobre la misma variante y el mismo destino.
      if (!f || f.variante.id !== variante.id || f.destino?.id !== destino.id) return f;
      return { ...f, conteo: ok ? data : undefined };
    });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo consultar el alcance", color: "error" });
    }
  };

  const abrirFusion = (variante) => {
    const destino = variante.sugerencia || null;
    setFusion({ variante, destino, conteo: null });
    if (destino) consultarConteo(variante, destino);
  };

  const elegirDestino = (destino) => {
    setFusion((f) => ({ ...f, destino, conteo: null }));
    if (destino) consultarConteo(fusion.variante, destino);
  };

  const fusionar = async () => {
    setFusionRun(true);
    try {
      const { ok, data } = await post("/api/marcasmodelos_fusionar", {
        marca: fusion.variante.marca,
        modelo: fusion.variante.modelo,
        id: fusion.destino.id,
        confirmar: true,
      });
      if (ok) {
        showAlert({ message: data.ok || "Fusión aplicada", color: "success" });
        setFusion(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo aplicar la fusión", color: "error" });
      }
    } finally {
      setFusionRun(false);
    }
  };

  const columns = [
    { field: "marca", headerName: "Marca", flex: 1, minWidth: 140 },
    { field: "modelo", headerName: "Modelo", flex: 1, minWidth: 140 },
    {
      field: "unidades",
      headerName: "Unidades vivas",
      width: 130,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "prestamos",
      headerName: "Préstamos activos",
      width: 150,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "sugerencia",
      headerName: "Parecido en el catálogo",
      flex: 1,
      minWidth: 180,
      sortable: false,
      valueGetter: (value) => (value ? etiquetaCatalogo(value) : "—"),
    },
    {
      field: "acciones",
      headerName: "",
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        puedeModificar && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<LibraryAddCheckOutlinedIcon />}
              onClick={() => setAgregando(params.row)}
              sx={btnOutline(FP.color.navy)}
            >
              Aceptar
            </Button>
            <Button
              size="small"
              startIcon={<CallMergeIcon />}
              disabled={!catalogo.length}
              onClick={() => abrirFusion(params.row)}
              sx={btnOutline(FP.color.navy)}
            >
              Fusionar
            </Button>
          </Stack>
        ),
    },
  ];

  // ---- Llevar al catálogo los modelos del inventario ----
  // Después de estandarizar, cada marca/modelo de las unidades disponibles
  // queda en el catálogo para ponerle precio con la carga masiva. Primero se
  // consulta cuántos entran; las filas con otra escritura de la misma clave
  // se renombran conservando su precio.
  const [desdeInv, setDesdeInv] = useState(null); // {nuevos, renombrados} o null
  const [desdeInvRun, setDesdeInvRun] = useState(false);
  const [catalogoKey, setCatalogoKey] = useState(0);
  const consultarDesdeInventario = async () => {
    const { ok, data } = await post("/api/marcasmodelos_catalogo_desde_inventario", {});
    if (ok) setDesdeInv(data);
    else showAlert({ message: data.Error || "No se pudo consultar el inventario", color: "error" });
  };
  const llevarAlCatalogo = async () => {
    setDesdeInvRun(true);
    try {
      const { ok, data } = await post("/api/marcasmodelos_catalogo_desde_inventario", {
        confirmar: true,
      });
      if (ok) {
        showAlert({ message: data.ok || "Catálogo actualizado", color: "success" });
        setDesdeInv(null);
        setCatalogoKey((k) => k + 1); // recarga la tabla del catálogo
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo actualizar el catálogo", color: "error" });
      }
    } finally {
      setDesdeInvRun(false);
    }
  };
  const totalDesdeInv = desdeInv ? (desdeInv.nuevos || 0) + (desdeInv.renombrados || 0) : 0;

  const conteo = fusion?.conteo;
  const totalAfectado = conteo ? (conteo.unidades || 0) + (conteo.prestamos || 0) : 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Marcas y Modelos"
        subtitle="Estandarización del inventario contra el catálogo"
        cta={
          tab === 0 && (
            <Button startIcon={<RefreshIcon />} onClick={cargar} sx={btnGhost}>
              Actualizar
            </Button>
          )
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
          >
            <Tab label="Variantes detectadas" />
            <Tab label="Catálogo" />
            <Tab label="Revisar con IA" />
          </Tabs>

          {tab === 0 && (
            <>
              <Typography
                sx={{
                  mb: 1.5,
                  fontWeight: 700,
                  color: FP.color.textSecondary,
                  fontSize: "0.85rem",
                }}
              >
                {variantes.length} variantes fuera del catálogo (solo datos vivos: unidades
                disponibles y préstamos activos; el histórico facturado no se toca)
              </Typography>
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={variantes}
                  columns={columns}
                  loading={loading}
                  rowHeight={46}
                  pageSizeOptions={[25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  slots={{ noRowsOverlay: SinVariantes }}
                  sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
                  disableRowSelectionOnClick
                />
              </Box>
            </>
          )}

          {tab === 1 && (
            <>
              {puedeModificar && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "center" }}
                  sx={{ mb: 2 }}
                >
                  <Typography sx={{ flex: 1, fontSize: "0.85rem", color: FP.color.textSecondary }}>
                    Lleve al catálogo las marcas y modelos de sus unidades disponibles para ponerles
                    precio con la carga masiva y aplicarlos al inventario.
                  </Typography>
                  <Button
                    startIcon={<PlaylistAddIcon />}
                    onClick={consultarDesdeInventario}
                    sx={btnOutline(FP.color.navy)}
                  >
                    Agregar modelos del inventario
                  </Button>
                </Stack>
              )}
              <CatalogosConfig key={catalogoKey} enHub soloCatalogo="marcamodelo" />
            </>
          )}
          {tab === 2 && <RevisionIA puedeModificar={puedeModificar} />}
        </Box>
      </Box>

      {/* Aceptar la variante como entrada canónica */}
      <Dialog
        open={agregando !== null}
        onClose={() => setAgregando(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Agregar al catálogo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <b>{agregando ? `${agregando.marca} ${agregando.modelo}`.trim() : ""}</b> se agregará al
            catálogo como entrada canónica y dejará de listarse como variante. El inventario no se
            modifica.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAgregando(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              agregandoRun ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <LibraryAddCheckOutlinedIcon />
              )
            }
            onClick={agregar}
            disabled={agregandoRun}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Llevar al catálogo los modelos del inventario */}
      <Dialog
        open={desdeInv !== null}
        onClose={() => setDesdeInv(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Agregar modelos del inventario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {totalDesdeInv === 0 ? (
              "Todas las marcas y modelos de sus unidades disponibles ya están en el catálogo."
            ) : (
              <>
                Se agregarán <b>{desdeInv?.nuevos || 0}</b> modelos al catálogo (sin precio) y se
                renombrarán <b>{desdeInv?.renombrados || 0}</b> que estaban escritos distinto
                (conservan su precio). El inventario no se modifica.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDesdeInv(null)} sx={btnGhost}>
            {totalDesdeInv === 0 ? "Cerrar" : "Cancelar"}
          </Button>
          {totalDesdeInv > 0 && (
            <Button
              startIcon={
                desdeInvRun ? <CircularProgress size={16} color="inherit" /> : <PlaylistAddIcon />
              }
              onClick={llevarAlCatalogo}
              disabled={desdeInvRun}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Agregar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Fusionar hacia una entrada del catálogo */}
      <Dialog
        open={fusion !== null}
        onClose={() => setFusion(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Fusionar con el catálogo</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            La variante{" "}
            <b>{fusion ? `${fusion.variante.marca} ${fusion.variante.modelo}`.trim() : ""}</b> se
            renombrará en el inventario con la entrada canónica que elija.
          </DialogContentText>
          <Autocomplete
            options={catalogo}
            value={fusion?.destino || null}
            onChange={(_, v) => elegirDestino(v)}
            getOptionLabel={etiquetaCatalogo}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Entrada canónica destino" sx={fpInputSx} />
            )}
          />
          {fusion?.destino && (
            <Box sx={{ mt: 2 }}>
              {conteo === null && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography sx={{ fontSize: "0.85rem", color: FP.color.textMuted }}>
                    Consultando el alcance…
                  </Typography>
                </Stack>
              )}
              {conteo && totalAfectado === 0 && (
                <DialogContentText>
                  No hay filas que cambiar: los datos vivos ya están escritos exactamente así.
                </DialogContentText>
              )}
              {conteo && totalAfectado > 0 && (
                <DialogContentText>
                  Se actualizarán <b>{conteo.unidades}</b> unidades del inventario y{" "}
                  <b>{conteo.prestamos}</b> préstamos activos. Las unidades vendidas, incautadas o
                  en tránsito y el histórico facturado no se tocan. Esta acción no se puede
                  deshacer.
                </DialogContentText>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setFusion(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              fusionRun ? <CircularProgress size={16} color="inherit" /> : <CallMergeIcon />
            }
            onClick={fusionar}
            disabled={fusionRun || !fusion?.destino || !conteo || totalAfectado === 0}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Fusionar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
